// Feeding calculator and calorie comparison in the page (issue 31). Reads the meals rendered by
// sections/cck-calculator.liquid and the typical pouch from assets/cck-comparison.json, and
// follows the customer's menu from the pincode gate (window.CCK_OUTCOME, `cck:region`).
import {
  compare,
  dailyKcal,
  KG_STEP,
  MAX_KG,
  MIN_KG,
  ownFood,
  plan,
  rupees,
} from "@cck/calculator-core";

/** Issue 410: the chip for the customer's own food. */
const OWN = "__own";

const root = document.querySelector("[data-cck-calc]");
const dataEl = document.getElementById("cck-calculator-data");

/** @type {{ comparisonUrl: string, meals: Record<"ncr" | "national", import("./cck-calculator-core.js").Meal[]> }} */
let data = { comparisonUrl: "", meals: { ncr: [], national: [] } };
try {
  data = JSON.parse(dataEl?.textContent ?? "");
} catch {
  // Leave the calculator showing its defaults.
}

const state = {
  name: "",
  kg: 4,
  age: /** @type {"kitten" | "adult" | "senior"} */ ("adult"),
  neutered: "yes",
  goal: /** @type {"maintain" | "lose" | "gain"} */ ("maintain"),
  meal: "Vitality",
  /** The last of my meals chosen: the comparison keeps showing it while "own food" is on. */
  lastMeal: "Vitality",
  /** @type {import("./cck-calculator-core.js").OwnFood} */
  own: { energy: 0, unit: "kcal", per: "100g", packG: 0 },
  /** @type {"ncr" | "national" | "none"} */ region: "ncr",
};

/** @type {import("./cck-calculator-core.js").Typical | null} */
let typical = null;

const $ = (/** @type {string} */ s) => document.querySelectorAll(s);
const setText = (/** @type {string} */ s, /** @type {string} */ t) => {
  for (const el of $(s)) el.textContent = t;
};

function regionFrom(/** @type {any} */ outcome) {
  return outcome?.region === "national" ? "national" : outcome?.region === "none" ? "none" : "ncr";
}

function render() {
  if (!root) return;
  const meals = data.meals[state.region === "national" ? "national" : "ncr"] ?? [];
  const meal = meals.find((m) => m.name === state.lastMeal) ?? meals[0];
  const own = state.meal === OWN;
  const cat = state.name.trim() || "your cat";
  const kcal = dailyKcal({
    kg: state.kg,
    age: state.age,
    neutered: state.neutered === "yes",
    goal: state.age === "kitten" ? "maintain" : state.goal,
  });

  setText("[data-cck-calc-cat]", cat);
  setText(
    "[data-cck-calc-cat-possessive]",
    state.name.trim() ? `${state.name.trim()}'s` : "your cat's",
  );
  for (const input of $("[data-cck-calc-kg-input]"))
    if (input instanceof HTMLInputElement && document.activeElement !== input)
      input.value = state.kg.toFixed(1);
  saveCat();
  setText("[data-cck-calc-kcal]", String(kcal));
  const goal = root.querySelector('[data-cck-calc-group="goal"]');
  if (goal instanceof HTMLElement) goal.hidden = state.age === "kitten";
  renderOwn(kcal, own);
  if (!meal) return;

  const p = plan(kcal, meal);
  setText("[data-cck-calc-meals-text]", `${p.perDay.toFixed(1)} ${meal.name}`);

  // The pick and its add-to-cart, only for a served menu.
  const pick = root.querySelector("[data-cck-calc-pick]");
  if (pick instanceof HTMLFormElement) {
    pick.hidden = own || state.region === "none" || !p.best;
    if (p.best) {
      setText(
        "[data-cck-calc-pick-text]",
        `My pick: a pack of ${p.best.units}, which lasts ${cat} about ${p.best.days} days.`,
      );
      const id = pick.querySelector("[data-cck-calc-variant]");
      if (id instanceof HTMLInputElement) id.value = String(p.best.variantId ?? "");
      setText(
        "[data-cck-calc-add]",
        `Add ${meal.name} × ${p.best.units} · ${rupees(p.best.price)}`,
      );
    }
  }

  // The comparison: its rows only once the real figures are in (issue 187).
  if (!typical) return;
  for (const el of $("[data-cck-compare-figures]"))
    if (el instanceof HTMLElement) el.hidden = false;
  for (const el of $("[data-cck-compare-loading]")) if (el instanceof HTMLElement) el.hidden = true;
  const perMeal = p.best ? p.best.price / p.best.units : undefined;
  const c = compare(kcal, meal, typical, perMeal);
  setText("[data-cck-compare-ours-label]", `${meal.name}, 70g`);
  setText("[data-cck-compare-ours]", c.ourPacks.toFixed(1));
  setText("[data-cck-compare-theirs]", c.theirPacks.toFixed(1));
  setText("[data-cck-compare-pouch-g]", String(typical.pouchG));
  const basis = document.querySelector("[data-cck-compare-basis]");
  if (basis && !basis.querySelector("a")) {
    const t = /** @type {any} */ (typical);
    basis.textContent =
      `Typical pouch: the median of ${t.products} complete wet cat foods sold in India that publish ` +
      `their calories: ${t.pouchG}g, about ${Math.round(t.kcalPerPouch)} kcal and ${rupees(t.pricePerPouch)} a pack. `;
    basis.append(
      Object.assign(document.createElement("a"), {
        href: t.sourcesUrl,
        textContent: "How it's worked out, with sources",
      }),
    );
  }
  for (const bar of $("[data-cck-compare-ours-bar]")) {
    if (bar instanceof HTMLElement) bar.style.width = `${c.ourBar}%`;
  }
  for (const bar of $("[data-cck-compare-theirs-bar]")) {
    if (bar instanceof HTMLElement) bar.style.width = `${c.theirBar}%`;
  }
  const cheaper = document.querySelector("[data-cck-compare-cheaper]");
  const other = document.querySelector("[data-cck-compare-other]");
  if (cheaper instanceof HTMLElement && other instanceof HTMLElement) {
    cheaper.hidden = !c.cheaper;
    other.hidden = c.cheaper;
    if (c.cheaper) {
      cheaper.innerHTML = "";
      cheaper.append(
        "That's ",
        Object.assign(document.createElement("strong"), {
          textContent: `${rupees(c.ourCost)} a day`,
        }),
        ` with ${meal.name}, against about ${rupees(c.theirCost)} in pouches. You save around ${rupees(c.theirCost - c.ourCost)} a day, and it's real food.`,
      );
    } else {
      other.textContent =
        state.region === "national"
          ? `Fewer packets, all of it real food. My national prices include frozen air shipping and packaging.`
          : `Fewer packets, all of it real food, cooked fresh in my kitchen.`;
    }
  }
}

/** Issue 410: the own-food fields and result, shown while that chip is chosen. */
function renderOwn(/** @type {number} */ kcal, /** @type {boolean} */ own) {
  if (!root) return;
  const show = (/** @type {string} */ s, /** @type {boolean} */ on) => {
    for (const el of root.querySelectorAll(s)) if (el instanceof HTMLElement) el.hidden = !on;
  };
  show("[data-cck-calc-own]", own);
  show("[data-cck-calc-own-result]", own);
  show("[data-cck-calc-own-disclaimer]", own);
  show("[data-cck-calc-meals-line]", !own);
  show("[data-cck-calc-disclaimer]", !own);
  if (!own) return;
  const f = ownFood(kcal, state.own);
  const one = (/** @type {number} */ n) => n.toFixed(1);
  let text = "Enter the calories and the pack's weight, and I'll work it out.";
  if (f && f.bag)
    text = `About ${Math.round(f.gramsPerDay)} g a day; a ${state.own.packG} g bag lasts about ${Math.round(f.bagDays ?? 0)} days.`;
  else if (f)
    text = `About ${one(f.packetsPerDay)} packets a day (${Math.round(f.gramsPerDay)} g), or ${Math.round(f.packetsPerWeek)} a week.`;
  if (f?.unusual)
    text +=
      " That figure looks unusual for cat food: check it's per 100 g or per pack as the pack says, and kcal or kJ.";
  setText("[data-cck-calc-own-result]", text);
}

function pressed(/** @type {Element} */ group, /** @type {string} */ value) {
  for (const b of group.querySelectorAll("[data-cck-calc-value]")) {
    b.setAttribute("aria-pressed", String(b.getAttribute("data-cck-calc-value") === value));
  }
}

function setUp() {
  if (!root) return;
  const name = root.querySelector("[data-cck-calc-name]");
  name?.addEventListener("input", () => {
    if (name instanceof HTMLInputElement) state.name = name.value.slice(0, 20);
    render();
  });
  for (const b of root.querySelectorAll("[data-cck-calc-kg]")) {
    b.addEventListener("click", () => {
      const dir = Number(b.getAttribute("data-cck-calc-kg"));
      state.kg = Math.min(
        MAX_KG,
        Math.max(MIN_KG, Math.round((state.kg + dir * KG_STEP) * 10) / 10),
      );
      render();
    });
  }
  // Typed straight in (issue 186): 4 to 8 kg is one edit, not eight taps.
  for (const input of root.querySelectorAll("[data-cck-calc-kg-input]")) {
    if (!(input instanceof HTMLInputElement)) continue;
    const take = () => {
      const v = Number(input.value.replace(",", "."));
      if (!Number.isFinite(v) || v <= 0) return;
      state.kg = Math.min(MAX_KG, Math.max(MIN_KG, Math.round(v * 10) / 10));
      render();
    };
    input.addEventListener("input", take);
    input.addEventListener("change", () => {
      take();
      input.value = state.kg.toFixed(1);
    });
  }
  for (const group of root.querySelectorAll("[data-cck-calc-group]")) {
    const key = group.getAttribute("data-cck-calc-group");
    for (const b of group.querySelectorAll("[data-cck-calc-value]")) {
      b.addEventListener("click", () => {
        const v = b.getAttribute("data-cck-calc-value") ?? "";
        if (key === "age") state.age = /** @type {any} */ (v);
        if (key === "neutered") state.neutered = v;
        if (key === "goal") state.goal = /** @type {any} */ (v);
        if (key === "meal") {
          state.meal = v;
          if (v !== OWN) state.lastMeal = v;
        }
        pressed(group, v);
        render();
      });
    }
  }
  for (const el of root.querySelectorAll("[data-cck-own]")) {
    if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement)) continue;
    const key = /** @type {keyof typeof state.own} */ (el.dataset.cckOwn);
    const v = state.own[key];
    if (v) el.value = String(v);
    el.addEventListener("input", () => {
      const own = /** @type {any} */ (state.own);
      own[key] =
        el instanceof HTMLInputElement ? Number(el.value.replace(",", ".")) || 0 : el.value;
      saveOwn();
      render();
    });
  }
  fetch(data.comparisonUrl)
    .then((r) => (r.ok ? r.json() : null))
    .then((t) => {
      if (!t) throw new Error("no comparison data");
      typical = t;
      render();
    })
    .catch(() => {
      // Without the typical pouch there are no figures to show: say so, never show samples.
      for (const el of $("[data-cck-compare-loading]")) {
        el.textContent = "The comparison couldn't load just now. ";
        el.append(
          Object.assign(document.createElement("a"), {
            href: "/pages/typical-wet-food",
            textContent: "The figures and their sources are here.",
          }),
        );
      }
    });
}

// The cat is remembered on this device (issue 186): the calculator opens on it next time, and
// meal pages say how much it needs ("Jinji needs about 2.3 Thrive a day").
const CAT = "cck:cat";
function saveCat() {
  try {
    localStorage.setItem(
      CAT,
      JSON.stringify({
        name: state.name.trim(),
        kg: state.kg,
        age: state.age,
        neutered: state.neutered,
        goal: state.goal,
        meal: state.meal,
      }),
    );
  } catch {
    // Storage blocked: nothing remembered.
  }
}
// Issue 410: the own food's numbers, on this device only (nothing is sent anywhere).
const OWN_FOOD = "cck:own-food";
function saveOwn() {
  try {
    localStorage.setItem(OWN_FOOD, JSON.stringify(state.own));
  } catch {
    // Storage blocked: nothing remembered.
  }
}
function restoreOwn() {
  try {
    const o = JSON.parse(localStorage.getItem(OWN_FOOD) ?? "null");
    if (!o || typeof o !== "object") return;
    if (Number.isFinite(o.energy) && o.energy > 0) state.own.energy = o.energy;
    if (Number.isFinite(o.packG) && o.packG > 0) state.own.packG = o.packG;
    if (o.unit === "kcal" || o.unit === "kJ") state.own.unit = o.unit;
    if (o.per === "100g" || o.per === "pack") state.own.per = o.per;
  } catch {
    // Nothing remembered.
  }
}
restoreOwn();

function restoreCat() {
  try {
    const c = JSON.parse(localStorage.getItem(CAT) ?? "null");
    if (!c || typeof c !== "object") return;
    if (typeof c.name === "string") state.name = c.name.slice(0, 20);
    if (Number.isFinite(c.kg)) state.kg = Math.min(MAX_KG, Math.max(MIN_KG, c.kg));
    if (["kitten", "adult", "senior"].includes(c.age)) state.age = c.age;
    if (["yes", "no"].includes(c.neutered)) state.neutered = c.neutered;
    if (["maintain", "lose", "gain"].includes(c.goal)) state.goal = c.goal;
    if (typeof c.meal === "string" && (data.meals.ncr ?? []).some((m) => m.name === c.meal))
      state.meal = state.lastMeal = c.meal;
    if (c.meal === OWN) state.meal = OWN;
  } catch {
    // Nothing remembered.
  }
  if (!root) return;
  const name = root.querySelector("[data-cck-calc-name]");
  if (name instanceof HTMLInputElement) name.value = state.name;
  for (const [key, value] of /** @type {[string, string][]} */ ([
    ["age", state.age],
    ["neutered", state.neutered],
    ["goal", state.goal],
    ["meal", state.meal],
  ])) {
    const group = root.querySelector(`[data-cck-calc-group="${key}"]`);
    if (group) pressed(group, value);
  }
}
restoreCat();

document.addEventListener("cck:region", (e) => {
  state.region = regionFrom(/** @type {CustomEvent} */ (e).detail?.outcome);
  render();
});
state.region = regionFrom(/** @type {any} */ (window).CCK_OUTCOME);
// A product page's "How much?" link preselects its meal: /pages/feeding-calculator?meal=Thrive
const preset = new URLSearchParams(location.search).get("meal");
if (preset && (data.meals.ncr ?? []).some((m) => m.name === preset)) {
  state.meal = state.lastMeal = preset;
  const group = root?.querySelector('[data-cck-calc-group="meal"]');
  if (group) pressed(group, preset);
}
setUp();
render();
